/**
 * Trades Journal SUBPAGE — port of approved mockup v0.5 (2026-07-13).
 *
 * Own page (#page-journal) reached via the ghost [Journal] button in the
 * Trades header. The Trades page itself is untouched (his hard rule) and its
 * bottom journal panel stays as-is; this subpage is ADDITIVE.
 *
 * Layout (mockup contract): realized hero (P&L + win-rate ring + context
 * sentence) → KPI chips (closed / profit factor / avg return / avg hold /
 * AI reviews) → horizon breakdown → filter chips with counts → expandable
 * closed-trade rows (detail chips, exits history, journal area).
 *
 * The per-trade journal area reuses ai-trade-journal.js verbatim: rows embed
 * the module's `tj-exp-<tradeId>` container, so aiReviewTrade / addJournalEntry /
 * saveJournalEntry / deleteJournalEntry work unchanged. The hidden legacy
 * journal table no longer renders (see renderTradeJournal gate in trades.js),
 * so these container ids exist exactly once in the DOM.
 *
 * Deterministic only — the hero context sentence is templated from real
 * numbers, never generated prose.
 */

import { S } from '../core/state.js';
import { esc, f, fU, fP, pc, fByCur, realizedUSD, realizedOrigin, tradeForRealized } from '../core/utils.js';
import { toggleJournalExpand, refreshExpandedJournals } from './ai-trade-journal.js';
import { thesisVerdict, watchSentence, hasThesis, VERDICTS } from '../core/thesis.js';

const $ = id => document.getElementById(id);

let _filter = 'all';

// ─── DATA ───────────────────────────────────────────────────────

/** Group S.realized exits per trade → one row per closed trade / partial-open trade. */
function _rows() {
  const rl = S.realized || [];
  const byTrade = new Map();
  rl.forEach(r => {
    const key = r.tradeId || ('orphan:' + (r.ticker || '?') + ':' + (r.date || ''));
    if (!byTrade.has(key)) byTrade.set(key, []);
    byTrade.get(key).push(r);
  });

  const rows = [];
  byTrade.forEach((exits, key) => {
    exits.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
    const last = exits[exits.length - 1];
    const t = tradeForRealized(last, S.trades);
    const cur = last.currency || 'USD';
    const pnl = exits.reduce((s, r) => s + (r.pnl || 0), 0);
    const pnlUSD = exits.reduce((s, r) => s + (realizedUSD(r)), 0);
    const qtySold = exits.reduce((s, r) => s + (r.qty || 0), 0);
    const entry = last.entry;
    const pnlP = (entry && qtySold) ? (pnl / (entry * qtySold) * 100) : null;
    const isTd = t && t.type === 'TimeDeposit';
    const open = t && (t.openDate || t.date);
    const close = last.date || (t && t.closeDate);
    const days = (open && close) ? Math.max(1, Math.round((new Date(close) - new Date(open)) / 86400000)) : null;
    const partial = !!(t && t.status !== 'Closed');
    rows.push({
      key, t, exits, cur, pnl, pnlUSD, pnlP, days, partial, isTd,
      // Where this row came from. `S.realized` is the whole book's ledger, not a trade list.
      origin: realizedOrigin(last),
      ticker: last.ticker || (t && t.ticker) || '?',
      entry, exit: last.exit,
      lev: (t && t.leverage) || 1,
      side: (t && t.side) || '—',
      hz: (t && t.horizon) || null,
      reason: isTd ? 'Matured' : (last.reason || '—'),
      date: last.date,
      journal: (t && t.journal) || [],
      win: pnlUSD >= 0,
    });
  });
  return rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

/**
 * ⚠ THE DISCIPLINE METRICS COUNT TRADES ONLY.
 *
 * Win rate, profit factor, average return and average hold are statements about how he TRADES.
 * Computed over every realized row they were statements about portfolio rebalancing instead:
 * 90 % over 21 rows, where 13 were `Exit Holding`. Trades alone: 100 % over 7. His call,
 * 2026-08-16 — "trade metriği trade'i ölçsün".
 *
 * The portfolio and deposit rows are NOT hidden. They keep their place in the list and get their
 * own totals, because dropping realized money out of a realized-P&L view would be the other
 * kind of lie.
 */
function _stats(rows) {
  const trades = rows.filter(r => r.origin === 'trade');
  const pf_ = rows.filter(r => r.origin === 'portfolio');
  const int_ = rows.filter(r => r.origin === 'interest');
  const closed = trades.filter(r => !r.partial);
  const wins = closed.filter(r => r.win);
  const losses = closed.filter(r => !r.win);
  const totUSD = rows.reduce((s, r) => s + r.pnlUSD, 0);
  const grossW = wins.reduce((s, r) => s + r.pnlUSD, 0);
  const grossL = Math.abs(losses.reduce((s, r) => s + r.pnlUSD, 0));
  const avg = a => a.length ? a.reduce((s, x) => s + x, 0) / a.length : null;
  const rets = closed.filter(r => r.pnlP != null).map(r => r.pnlP);
  const daysOf = a => a.filter(r => r.days != null).map(r => r.days);
  const aiReviewed = closed.filter(r => r.journal.some(e => e.type === 'ai_review')).length;
  return {
    closed, wins, losses, totUSD,
    // totUSD stays the WHOLE book's realized P&L — the hero says "TOTAL REALIZED P&L" and it
    // must keep meaning that. The split is beside it, never instead of it.
    tradeUSD: trades.reduce((s2, r) => s2 + r.pnlUSD, 0),
    pfUSD: pf_.reduce((s2, r) => s2 + r.pnlUSD, 0),
    intUSD: int_.reduce((s2, r) => s2 + r.pnlUSD, 0),
    nTrades: trades.length, nPortfolio: pf_.length, nInterest: int_.length,
    winRate: closed.length ? Math.round(wins.length / closed.length * 100) : null,
    pf: grossL > 0 ? grossW / grossL : (grossW > 0 ? Infinity : null),
    avgRet: avg(rets),
    avgHold: avg(daysOf(closed)),
    avgHoldW: avg(daysOf(wins)),
    avgHoldL: avg(daysOf(losses)),
    aiReviewed,
  };
}

function _matches(r) {
  if (_filter === 'win') return r.win;
  if (_filter === 'loss') return !r.win;
  if (_filter === 'sl') return String(r.reason).toUpperCase() === 'SL';
  if (_filter === 'nojrn') return !r.journal.length;
  return true;
}

// ─── ACTIONS (wired via data-action in main.js) ─────────────────

export function openJournal() {
  if (window._navActivateTab) window._navActivateTab('journal');
}

export function jrnFilter(key) {
  _filter = key || 'all';
  renderJournal();
}

export function jrnRowToggle(tradeKey, tradeId) {
  const row = $('jrow-' + tradeKey);
  if (!row) return;
  const opening = !row.classList.contains('open');
  row.classList.toggle('open');
  if (opening && tradeId) {
    const area = $('tj-exp-' + tradeId);
    // First open: let ai-trade-journal render its action bar + entries.
    if (area && !area.innerHTML) toggleJournalExpand(tradeId);
    else if (area) area.style.display = 'block';
  }
}

// ─── RENDER ─────────────────────────────────────────────────────

function _heroContext(st, rows) {
  if (!st.closed.length) return 'No closed trades yet. Exits will appear here as they are booked.';
  const tpWins = st.wins.filter(r => /^TP/i.test(String(r.reason))).length;
  const slLoss = st.losses.filter(r => String(r.reason).toUpperCase() === 'SL').length;
  const best = [...rows].sort((a, b) => b.pnlUSD - a.pnlUSD)[0];
  const worst = [...rows].sort((a, b) => a.pnlUSD - b.pnlUSD)[0];
  let s = `${st.closed.length} closed trade${st.closed.length > 1 ? 's' : ''}.`;
  if (st.wins.length && tpWins) s += ` <b>${tpWins} of ${st.wins.length} wins</b> exited at TP levels.`;
  if (slLoss) s += ` ${slLoss} loss${slLoss > 1 ? 'es' : ''} stopped out at SL.`;
  if (best && best.pnlUSD > 0) s += ` Best: ${esc(best.ticker)} +${fU(best.pnlUSD)}`;
  if (worst && worst.pnlUSD < 0) s += ` · Worst: ${esc(worst.ticker)} ${fU(worst.pnlUSD)}`;
  return s + '.';
}

function _hero(st, rows) {
  const wr = st.winRate != null ? st.winRate : 0;
  const C = 292; // ring circumference (r=46.5)
  const off = Math.round(C * (1 - wr / 100));
  return `
    <div class="tjr-hero">
      <div class="tjr-hleft">
        <div class="tjr-hlbl">TOTAL REALIZED P&amp;L</div>
        <div class="tjr-hnum ${pc(st.totUSD)}">${st.totUSD >= 0 ? '+' : ''}${fU(st.totUSD)}</div>
        <div class="tjr-hctx">${_heroContext(st, rows)}</div>
      </div>
      <div class="tjr-ring">
        <svg width="112" height="112" viewBox="0 0 112 112">
          <defs><linearGradient id="tjrGradR" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="#00f0ff"/><stop offset="100%" stop-color="#34e2a0"/>
          </linearGradient></defs>
          <circle class="bgc" cx="56" cy="56" r="46.5"/>
          <circle class="fgc" cx="56" cy="56" r="46.5" style="stroke-dashoffset:${off}"/>
        </svg>
        <div class="rlbl"><b>${st.winRate != null ? st.winRate + '%' : '—'}</b><span>WIN RATE</span></div>
      </div>
    </div>`;
}

function _kpis(st) {
  const pfTxt = st.pf == null ? '—' : (st.pf === Infinity ? '∞' : f(st.pf));
  const k = (l, v, cls, s) => `<div class="tjr-kpi"><div class="l">${l}</div><div class="v ${cls || ''}">${v}</div><div class="s">${s || ''}</div></div>`;
  return `<div class="tjr-kpis">
    ${k('CLOSED', st.closed.length, 'cy', `${st.wins.length} wins · ${st.losses.length} losses`)}
    ${k('PROFIT FACTOR', pfTxt, st.pf != null && st.pf >= 1 ? 'pos' : 'neg', 'gross win / loss')}
    ${k('AVG RETURN', st.avgRet != null ? fP(st.avgRet) : '—', st.avgRet >= 0 ? 'pos' : 'neg', 'per closed trade')}
    ${k('AVG HOLD', st.avgHold != null ? Math.round(st.avgHold) + 'd' : '—', 'gold',
      st.avgHoldW != null || st.avgHoldL != null
        ? `wins ${st.avgHoldW != null ? Math.round(st.avgHoldW) + 'd' : '—'} · losses ${st.avgHoldL != null ? Math.round(st.avgHoldL) + 'd' : '—'}` : '')}
    ${k('AI REVIEWS', `${st.aiReviewed} / ${st.closed.length}`, 'vio', `${st.closed.length - st.aiReviewed} unreviewed`)}
  </div>`;
}

function _horizon(st) {
  const groups = [['short', 'SHORT <2W'], ['medium', 'MEDIUM 2W-3M'], ['long', 'LONG >3M']];
  const cells = groups.map(([hz, label]) => {
    const g = st.closed.filter(r => r.hz === hz);
    if (!g.length) return `<div class="tjr-hzc"><div class="n">${label}</div><div class="d">no closed trades</div></div>`;
    const w = g.filter(r => r.win).length;
    const usd = g.reduce((s, r) => s + r.pnlUSD, 0);
    const dd = g.filter(r => r.days != null);
    const ad = dd.length ? Math.round(dd.reduce((s, r) => s + r.days, 0) / dd.length) : null;
    return `<div class="tjr-hzc"><div class="n">${label}</div><div class="d">${g.length} trade${g.length > 1 ? 's' : ''} · <b class="w">${Math.round(w / g.length * 100)}% win</b> · <b class="${usd >= 0 ? 'p' : 'neg'}">${usd >= 0 ? '+' : ''}${fU(usd)}</b>${ad != null ? ' · avg ' + ad + 'd' : ''}</div></div>`;
  }).join('');
  return `<div class="tjr-hz">${cells}</div>`;
}

function _filters(rows) {
  const n = k => rows.filter(r => { const keep = _filter; _filter = k; const m = _matches(r); _filter = keep; return m; }).length;
  const b = (k, label) => `<button class="${_filter === k ? 'on' : ''}" data-action="jrnFilter" data-args='["${k}"]'>${label} ${n(k)}</button>`;
  return `<div class="tjr-qhd">CLOSED TRADES · NEWEST FIRST
    <div class="tjr-flt">${b('all', 'ALL')}${b('win', 'WINS')}${b('loss', 'LOSSES')}${b('sl', 'SL')}${b('nojrn', 'NO JOURNAL')}</div>
  </div>`;
}

function _reasonBdg(r) {
  const up = String(r.reason).toUpperCase();
  if (r.partial) return '<span class="tjr-bdg part">PARTIAL · OPEN</span>';
  if (/^TP/.test(up)) return `<span class="tjr-bdg tp">${esc(up)}</span>`;
  if (up === 'SL') return '<span class="tjr-bdg sl">SL</span>';
  if (up === 'MATURED') return '<span class="tjr-bdg tp">MATURED</span>';
  return `<span class="tjr-bdg man">${esc(up)}</span>`;
}

/**
 * Where the row came from, said out loud.
 *
 * Trades carry no badge — they are what the page is for, and badging the majority case is noise.
 * A portfolio exit or a deposit maturity says so, because the alternative is what he hit on
 * 2026-08-16: reading HVZ on the Trades journal and asking why a portfolio position was there.
 */
function _originBdg(r) {
  if (r.origin === 'portfolio') return '<span class="tjr-bdg org-pf">PORTFOLIO</span>';
  if (r.origin === 'interest') return '<span class="tjr-bdg org-int">DEPOSIT</span>';
  return '';
}

function _dg(l, v, cls) {
  return v == null || v === '' ? '' : `<div class="tjr-dg"><div class="l">${l}</div><div class="v ${cls || ''}">${v}</div></div>`;
}


/**
 * THE THESIS BLOCK — why the trade was opened, and whether that reason held.
 *
 * Rendered above JOURNAL in an expanded row. The verdict is DERIVED by `core/thesis.js` from the
 * exit this row already carries; nothing is stored and no watcher runs. A trade opened before the
 * field existed shows NOT RECORDED and is never back-filled with a guess.
 */
function _thesis(r) {
  // ⚠ A ROW WITH NO TRADE RECORD IS NOT A TRADE, AND MUST NOT BE OFFERED A THESIS.
  //
  // `_rows()` builds this page from `S.realized`, which is the WHOLE BOOK's realized-P&L ledger
  // — trades, portfolio `Exit Holding` rows and time-deposit maturities alike, keyed
  // `orphan:<ticker>:<date>` when there is no `tradeId`. Measured on the 2026-08-14 backup:
  // of 21 rows, **13 are portfolio holding exits** (VOO QQQ SMH XLV XLE COPX DBA CLOU AFA IDH
  // OTJ YKT HVZ) and one is a deposit maturity.
  //
  // The first version of this block rendered on all of them, telling him a position he sold out
  // of the PORTFOLIO was "opened before the thesis was captured" and inviting him to add one.
  // HVZ was never a trade; it has no entry thesis and never will. He caught it the same day
  // (PROC-012 — a fix's output deserves the same measurement as the thing it fixed).
  //
  // `_row` already has the precedent: no `tid` means "Original trade record not found — journal
  // unavailable for this exit." The thesis follows the same rule and renders nothing at all.
  if (!r.t) return '';
  const th = r.t.thesis;
  const v = thesisVerdict(th, { exit: r.exit, reason: r.reason });
  const vd = VERDICTS[v] || VERDICTS.none;
  const head = `<div class="thx-bh"><span class="thx-cap">THESIS</span>`
    + `<span class="thx-vd ${vd.cls}">${vd.label}</span></div>`;

  if (!hasThesis(th)) {
    return `<div class="thx-blk dead">${head}<div class="thx-q mut">Opened before the thesis was `
      + `captured. Nothing is assumed and nothing is back-filled.</div></div>`;
  }

  // Deterministic chips, templated from what was written — never generated prose.
  const chip = (k, val) => val ? `<span><i>${k}</i>${esc(val)}</span>` : '';
  const sentence = watchSentence(th);
  const chips = [
    chip('trigger', th.trigger),
    chip('expected', th.expected),
    chip('invalidation', th.invalidation),
    // ⚠ SAY WHICH OBSERVATION FIRED IT. `firedAt` means the watcher saw it cross while the trade
    // was OPEN; without a stamp, `inv` means the EXIT crossed it. Those are different facts and
    // the row has to be able to tell him which one it is looking at.
    sentence
      ? `<span class="${v === 'inv' ? 'fired' : ''}"><i>watch</i>${esc(sentence)}`
        + `${th.firedAt ? ' · FIRED ' + String(th.firedAt).slice(0, 10) : (v === 'inv' ? ' · HIT AT EXIT' : '')}</span>`
      : '<span><i>watch</i>none set — judged by you</span>',
  ].filter(Boolean).join('');

  return `<div class="thx-blk">${head}`
    + (th.text ? `<div class="thx-q">${esc(th.text)}</div>` : '')
    + `<div class="thx-drv">${chips}</div></div>`;
}

function _row(r) {
  const fc = v => v == null ? '—' : fByCur(r.cur, v);
  const jn = r.journal.length;
  const hasAI = r.journal.some(e => e.type === 'ai_review');
  const ac = r.partial ? 'var(--cyan)' : (r.win ? 'var(--grn)' : 'var(--red)');
  const dt = r.date ? new Date(r.date).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : '—';
  const usdSub = r.cur !== 'USD' ? `${r.pnlUSD >= 0 ? '+' : ''}${fU(r.pnlUSD)} · ` : '';
  const sumMain = r.cur !== 'USD'
    ? `${r.pnl >= 0 ? '+' : ''}${fc(r.pnl)}${r.pnlP != null ? ' · ' + fP(r.pnlP) : ''}`
    : `${r.pnlUSD >= 0 ? '+' : ''}${fU(r.pnlUSD)}${r.pnlP != null ? ' · ' + fP(r.pnlP) : ''}`;
  const exitsLine = r.exits.length > 1 || r.partial
    ? `<div class="tjr-exits"><b>${r.partial ? 'Exits so far:' : 'Exits:'}</b> ${r.exits.map(e =>
        `${e.date ? new Date(e.date).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : '—'} · ${e.qty != null ? f(e.qty) : '—'} @ ${e.price != null ? fc(e.price) : '—'} (${esc(e.reason || '—')}, ${(e.pnl || 0) >= 0 ? '+' : ''}${fc(e.pnl || 0)})`).join(' → ')}</div>`
    : '';
  const tid = r.t ? r.t.id : null;
  const args = JSON.stringify([r.key, tid]);
  return `
  <div class="tjr-row" id="jrow-${esc(r.key)}" style="--ac:${ac}">
    <div class="tjr-rmain" data-action="jrnRowToggle" data-args='${args.replace(/'/g, '&#39;')}'>
      <span class="tjr-dot"></span><span class="tjr-tk">${esc(r.ticker)}</span>
      <span class="tjr-rside">${r.isTd ? 'TIME DEPOSIT' : `${esc(String(r.side).toUpperCase())} · ${r.lev}x`} · ${esc(r.cur)}</span>
      ${_reasonBdg(r)}${_originBdg(r)}
      <span class="tjr-jbadge ${hasAI ? 'ai' : ''}">${jn} 📝</span>
      <span class="tjr-rsum"><span class="${pc(r.pnlUSD)}">${sumMain}</span><small>${usdSub}${dt}${r.partial ? ' · trade still open' : (r.days != null ? ' · held ' + r.days + 'd' : '')}</small></span>
      <span class="tjr-chev">▾</span>
    </div>
    <div class="tjr-rdetail"><div class="tjr-rinner">
      <div class="tjr-dgrid">
        ${_dg('ENTRY', r.entry != null ? fc(r.entry) : null)}
        ${_dg('EXIT', r.exit != null ? fc(r.exit) : null)}
        ${_dg('QTY', r.exits.length === 1 && r.exits[0].qty != null ? f(r.exits[0].qty) : null)}
        ${r.lev > 1 ? _dg('LEV', r.lev + 'x') : ''}
        ${_dg('P&amp;L', `${r.pnl >= 0 ? '+' : ''}${fc(r.pnl)}`, pc(r.pnl))}
        ${r.cur !== 'USD' ? _dg('P&amp;L USD', `${r.pnlUSD >= 0 ? '+' : ''}${fU(r.pnlUSD)}`, pc(r.pnlUSD)) : ''}
        ${_dg('P&amp;L %', r.pnlP != null ? fP(r.pnlP) : null, pc(r.pnlP))}
        ${_dg('DAYS', r.days != null ? r.days : null)}
        ${r.hz ? _dg('HORIZON', esc(r.hz[0].toUpperCase() + r.hz.slice(1))) : ''}
        ${_dg('REASON', esc(String(r.reason)))}
      </div>
      ${exitsLine}
      ${_thesis(r)}
      <div class="tjr-jhd">JOURNAL${r.partial ? ' <span class="tjr-mut">AI review unlocks when the trade closes</span>' : ''}</div>
      ${tid
        ? `<div id="tj-exp-${esc(tid)}" class="tjr-jarea" style="display:none"></div>`
        : '<div class="tjr-mut">Original trade record not found — journal unavailable for this exit.</div>'}
    </div></div>
  </div>`;
}

export function renderJournal() {
  const host = $('jrnRows');
  if (!host) return;

  const rows = _rows();
  const st = _stats(rows);

  // header meta + Trades-header button count
  const meta = $('jrnMeta');
  // ⚠ The counts say which population each number is over. "win rate" is TRADES only.
  if (meta) meta.innerHTML = `${st.nTrades} trade${st.nTrades === 1 ? '' : 's'}`
    + `${st.nPortfolio ? ' · ' + st.nPortfolio + ' portfolio' : ''}`
    + `${st.nInterest ? ' · ' + st.nInterest + ' deposit' : ''}`
    + ` · <b>${st.totUSD >= 0 ? '+' : ''}${fU(st.totUSD)}</b> realized`
    + `${st.winRate != null ? ' · win rate ' + st.winRate + '% <span class="tjr-mut">(trades)</span>' : ''}`;
  const cnt = $('trJrnBtnN');
  if (cnt) cnt.textContent = st.closed.length || '';

  $('jrnHero').innerHTML = _hero(st, rows);
  $('jrnKpis').innerHTML = _kpis(st);
  $('jrnHz').innerHTML = _horizon(st);
  $('jrnFilt').innerHTML = rows.length ? _filters(rows) : '';

  const visible = rows.filter(_matches);
  host.innerHTML = rows.length
    ? (visible.map(_row).join('') || '<div class="tjr-empty">No trades match this filter.</div>')
    : '<div class="tjr-empty">No closed trades yet. Exit a trade and it will show up here with its full story.</div>';

  // Re-render any journal areas that were expanded before this render.
  refreshExpandedJournals();
  // Journal areas live inside collapsed row bodies; reopen rows whose area is expanded.
  visible.forEach(r => {
    if (r.t && document.getElementById('tj-exp-' + r.t.id) && document.getElementById('tj-exp-' + r.t.id).style.display === 'block') {
      const row = $('jrow-' + r.key);
      if (row) row.classList.add('open');
    }
  });
}
